<script>
import i18n from "@/plugins/i18n";
import InputField from "./input-field.vue";
import InputFieldHeader from "./input-field-header.vue";
import YearlyInputField from "./yearly-input-field.vue";
import YearlyInputFieldHeader from "./yearly-input-field-header.vue";
import { mapActions } from "vuex";

export default {
    props: {
        Metric: {
            type: Object,
            required: true,
        },

        Years: {
            type: Object,
            required: true,
        },
        
        Year: {
            type: Number,
            required: true,
        },

        SelectedFacilityId: {
            type: Number,
            required: true,
        },

        GeneralColorCode: {
            type: String,
            required: true,
        },
    },

    components: {
        InputField,
        InputFieldHeader,
        YearlyInputField,
        YearlyInputFieldHeader,
    },

    data() {
        return {
            details: null,
        }
    },

    computed: {
        QUARTERS() {
            return [
                {
                    value: 5,
                    text: i18n.t('kpisMetrics.yearly_short'),
                },
                {
                    value: 1,
                    text: 'Q1',
                },
                {
                    value: 2,
                    text: 'Q2',
                },
                {
                    value: 3,
                    text: 'Q3',
                },
                {
                    value: 4,
                    text: 'Q4',
                },
            ];
        },
    },

    methods: {
        ...mapActions('KPIsMetrics', [
            'getInputDetails',
        ]),

        EmitEvent(value, event) {
            Object.assign(value, {
                value_type: this.Metric.value_type,
                unit_type: this.Metric.unit_type,
                sust_metric_kpi_id: this.Metric.sust_metric_kpi_id,
                sust_sub_metric_kpi_id: this.Metric.sust_sub_metric_kpi_id,
                metric_kpi_id: this.Metric.sust_metric_kpi_id,
                sub_metric_kpi_id: this.Metric.sust_sub_metric_kpi_id,
            });

            this.$emit(event, value);
        },

        async ShowDetails(data) {
            await this.getInputDetails(data.metrics_input_table_id)
                .then(res => {
                    this.details = res.data;
                })
                .catch(() => {
                    this.$notify('error', '', true);
                })
                .finally(() => {
                    if (this.details) {
                        this.$refs.show_details.openPopup();
                    }
                })
        }
    }
}
</script>

<template>
    <div class="metric-input-field-group">
        <div v-if="Year">
            <div class="quarter-year">
                {{ Year }}
            </div>
            <div class="header flex-all-centered-row">
                <div class="metric-input-field-container"
                    v-for="quarter in QUARTERS" :key="quarter.value"
                >
                    <input-field-header 
                        :key="SelectedFacilityId"
                        :Quarter="quarter" 
                        :Year="Year" 
                        :Inputs="Metric.company_inputs"
                        :SelectedFacilityId="SelectedFacilityId" 
                        :GeneralColorCode="GeneralColorCode"
                    />
                </div>
            </div>
            <div class="cell flex-all-centered-row">
                <div class="metric-input-field-container"
                    v-for="quarter in QUARTERS" :key="quarter"
                >
                    <input-field 
                        :key="SelectedFacilityId"
                        :Quarter="quarter" 
                        :Year="Year" 
                        :Inputs="Metric.company_inputs"        
                        :SelectedFacilityId="SelectedFacilityId" 
                        :GeneralColorCode="GeneralColorCode"
                        :ValueType="Metric.value_type" 
                        @delete="EmitEvent($event, 'delete')"
                        @update="EmitEvent($event, 'update')"
                        @create="EmitEvent($event, 'create')"
                        @show_details="ShowDetails($event)"
                    />
                </div>
            </div>
        </div>
        
        <div v-if="Years">
            <div class="header flex-all-centered-row">
                <div class="metric-input-field-container"
                    style="background-color: var(--sustainfinity-navy); color: whitesmoke;"
                    v-for="year in Years" :key="year"
                >
                    <yearly-input-field-header 
                        :key="SelectedFacilityId"
                        :Year="year" 
                        :Inputs="Metric.company_inputs"
                        :SelectedFacilityId="SelectedFacilityId" 
                        :GeneralColorCode="GeneralColorCode"
                    />
                </div>
            </div>
            <div class="cell flex-all-centered-row">
                <div class="metric-input-field-container"
                    v-for="year in Years" :key="year"
                >
                    <yearly-input-field  
                        :key="SelectedFacilityId"
                        :Year="year" 
                        :Inputs="Metric.company_inputs"        
                        :SelectedFacilityId="SelectedFacilityId" 
                        :GeneralColorCode="GeneralColorCode"
                        :ValueType="Metric.value_type" 
                        @delete="EmitEvent($event, 'delete')"
                        @update="EmitEvent($event, 'update')"
                        @create="EmitEvent($event, 'create')"
                        @show_details="ShowDetails($event)"
                    />
                </div>
            </div>
        </div>

        <sust-popup
            ref="show_details"
            :title="$t('kpisMetrics.details.title')"
            size="sm"
            @onHide="() => details = null"
        >
            <div v-if="details" class="flex-all-centered-col">
                <div class="flex-all-centered-row" v-for="key in ['created', 'updated']" :key="key">
                    <div class="flex-all-centered-col" style="padding: .3em 0;">
                        <div style="font-weight: bold; color: black; font-size: 1.1em;">
                            {{ $t(`kpisMetrics.details.${key}_by_name`) }}
                        </div>
                        <div style="font-size: .95em;">
                            {{ details[`${key}_by_name`] || '-' }}
                        </div>
                    </div>
                    <div class="flex-all-centered-col" style="padding: .3em 0;">
                        <div style="font-weight: bold; color: black; font-size: 1.1em;">
                            {{ $t(`kpisMetrics.details.${key}_by_email`) }}
                        </div>
                        <div style="font-size: .95em;">
                            {{ details[`${key}_by_email`] || '-' }}
                        </div>
                    </div>
                    <div class="flex-all-centered-col">
                        <div style="font-weight: bold; color: black; font-size: 1.1em;">
                            {{ $t(`kpisMetrics.details.${key}_on`) }}
                        </div>
                        <div v-if="details[`${key}_on`]" style="font-size: .95em;">
                            {{ details[`${key}_on`] | dateFormat }}
                        </div>
                        <div v-else>
                            -
                        </div>
                    </div>
                </div>
            </div>
        </sust-popup>
    </div>
</template>

<style lang='scss' scoped>
.metric-input-field-group {
    width: 100%;
    border-radius: 1em;
    overflow: hidden;
    border: thin solid var(--sustainfinity-navy);
    margin-bottom: 1em;
    
    > div {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;

        > div {
            width: 100%;
            text-align: center;
        }
        
        > .quarter-year {
            background-color: var(--sustainfinity-navy);
            color: whitesmoke;
            border-bottom: thin solid whitesmoke;
            padding: .5em;
        }
        
        .header {
            border-bottom: thin solid lightgray;
        }
        
        > .header, .cell {
            justify-content: flex-start;
            align-items: flex-start;
            background-color: whitesmoke;
            color: var(--sustainfinity-navy);

            > .metric-input-field-container {
                width: 100%;
                padding: .5em;
                border-right: thin solid lightgray;
            }
        }

        > .cell {
            background-color: whitesmoke !important;
            color: var(--sustainfinity-navy) !important;
        }
    }
}
</style>